<template>
  <div>
    <div class="clearSelection">
      <button class="c-button__item c-button__item--outline" @click="clearSelection">
        {{ $t('pages.companies.analysis.table.clearSelection') }}
      </button>
    </div>
    <div v-show="items.length === 0 && !loading" v-text="$t('messages.noData')" />
    <div v-show="items.length !== 0" class="analysis-table">
      <div class="analysis-table-controller" :style="{ width: controller.width, top: controller.top }">
        <button
          v-if="controller.prev"
          type="button"
          class="analysis-table-controller-prev"
          @mousedown="scrollPrevStart" @mouseup="scrollEnd"
        >
          <KkIcon name="prev" color="#3A3B3F" />
        </button>
        <button
          v-if="controller.next"
          type="button"
          class="analysis-table-controller-next"
          @mousedown="scrollNextStart" @mouseup="scrollEnd"
        >
          <KkIcon name="next" color="#3A3B3F" />
        </button>
      </div>
      <div ref="container" class="analysis-table-container">
        <table ref="table">
          <thead ref="thead">
            <tr>
              <template v-for="(head, headIndex) in crossSearchFields" :key="head.name">
                <th v-if="headIndex === 0" :key="head.name" ref="searchFields" class="fixed" rowspan="2">
                  {{ head.name }}
                </th>
                <th v-else class="fixed" rowspan="2" :style="{ 'left': firstSearchFieldWidth }">
                  {{ head.name }}
                </th>
              </template>
              <th rowspan="2" class="answeredCountTh">
                <button
                  class="p-table__content__sort"
                  :class="orderCss(answeredCount.id)"
                  @click="sortTables(answeredCount.id)"
                >
                  {{ answeredCount.name }}
                </button>
              </th>
              <th v-for="head in staticHeadsExceptAnsweredCount" :key="'head_' + head.id"
                  :rowspan="calcRowspan(head)"
                  :colspan="calcColspan(head)"
                  class="questionNameTh"
              >
                <p>
                  Q{{ head.position }}.
                  <span>
                    {{ tableHeadText(head) }}
                  </span>
                </p>
                <p>
                  {{ head.name }}
                </p>
              </th>
            </tr>
            <tr>
              <th v-for="option in options"
                  :key="'head_' + option.headId +'option_' + option.id"
                  :class="{ 'optionThFirst': option.key === 0 }"
                  class="optionTh"
              >
                {{ option.name }}
              </th>
            </tr>
            <tr class="p-table__content__space">
              <td colspan="5" />
            </tr>
          </thead>
          <tbody v-show="!loading" ref="tbody">
            <tr v-if="headItem" class="head-item">
              <template v-for="(field, fieldIndex) in headItem.fields" :key="fieldIndex">
                <template v-if="fieldIndex === 0">
                  <td v-if="displayCheckbox" :key="fieldIndex" class="u-nowrap table-head fixed">
                    <ul class="c-checkbox">
                      <li class="c-checkbox__item">
                        <input
                          id="searchItemsHeader"
                          v-model="innerSearchItems"
                          type="checkbox"
                          class="c-checkbox__input"
                          :value="headItem.id"
                        >
                        <label class="c-checkbox__label" for="searchItemsHeader">
                          <span class="index-number">1. </span>
                          {{ field.name }}
                        </label>
                      </li>
                    </ul>
                  </td>
                  <td v-else class="u-nowrap table-head fixed">
                    <span class="index-number">1. </span>
                    {{ field.name }}
                  </td>
                </template>
                <td v-else class="table-head fixed"
                    :style="{ 'left': firstSearchFieldWidth }"
                >
                  {{ field.name }}
                </td>
              </template>
              <CompaniesAnalysisSurveysTableData
                :number="headItem.answersCount"
                :label="$t('components.CompaniesAnalysisSurveysTable.answerTotalCount')"
              />
              <template v-for="question in headItem.questions">
                <CompaniesAnalysisSurveysTableData
                  v-if="question.type === 'nps'"
                  :key="'question_' + question.id"
                  :number="question.value.score"
                  :color-tip-condition="panelColorClassCondition"
                  :label="$t('components.CompaniesAnalysisSurveysTable.npsAverage')"
                  class="optionTdFirst"
                />
                <template v-else>
                  <CompaniesAnalysisSurveysTableData
                    v-for="(option,optionKey) in question.options"
                    :key="'question_' + question.id +'_option_' + option.id"
                    :number="option.value"
                    :label="$t('components.CompaniesAnalysisSurveysTable.answerCount')"
                    :class="{ 'optionTdFirst': optionKey === 0 }"
                  />
                </template>
              </template>
            </tr>
            <template v-for="(item, itemIndex) in sortedItemList" :key="itemIndex">
              <tr>
                <template v-for="(field, fieldIndex) in item.fields" :key="fieldIndex">
                  <template v-if="fieldIndex === 0">
                    <td v-if="displayCheckbox" class="u-nowrap table-head fixed">
                      <div class="table-container">
                        <ul class="c-checkbox">
                          <li class="c-checkbox__item">
                            <input
                              :id="'searchItems' + itemIndex"
                              v-model="innerSearchItems"
                              type="checkbox"
                              class="c-checkbox__input"
                              :value="item.id"
                            >
                            <label class="c-checkbox__label" :for="'searchItems' + itemIndex">
                              <span class="index-number">{{ itemIndex + 2 + '. ' }}</span>
                              <a @click.prevent="changeIndividualAnalysisField(field)">{{ field.name }}</a>
                              <StoresCount v-if="item.fields.length === 1 && !!item.storeNames" :store-names="item.storeNames" :is-omit-single-store="isStoreAnalysisAxis" @open-modal="openModal" />
                            </label>
                          </li>
                        </ul>
                        <router-link
                          :to="{
                            name: 'CompaniesAnalysisSurveysDetail',
                            params: {
                              company_uuid: companyUuid,
                            },
                            query: {
                              analysis_type: item.fields[0].key,
                              analysis_id: item.fields[0].id,
                              survey_id: surveyId,
                              start_date: startDate,
                              end_date: endDate
                            }
                          }"
                          class="survey-link"
                        >
                          <KkIcon name="arrowRightLink" color="#3A3B3F" />
                        </router-link>
                      </div>
                    </td>
                    <td v-else class="u-nowrap table-head fixed">
                      <span class="index-number">{{ itemIndex + 2 + '. ' }}</span>
                      {{ field.name }}
                      <StoresCount v-if="item.fields.length === 1 && !!item.storeNames" :store-names="item.storeNames" :is-omit-single-store="isStoreAnalysisAxis" @open-modal="openModal" />
                    </td>
                  </template>
                  <td v-else class="u-nowrap table-head fixed" :style="{ 'left': firstSearchFieldWidth }">
                    {{ field.name }}
                    <StoresCount v-if="!!item.storeNames" :store-names="item.storeNames" :is-omit-single-store="isStoreAnalysisAxis" @open-modal="openModal" />
                  </td>
                </template>
                <CompaniesAnalysisSurveysTableData
                  :number="item.answersCount"
                />
                <template v-for="question in item.questions">
                  <template v-for="(answerItem, optionKey) in question.answerItems" :key="answerItem.key">
                    <CompaniesAnalysisSurveysTableData
                      v-bind="answerItem"
                      :class="{ 'optionTdFirst': optionKey === 0 }"
                    />
                  </template>
                </template>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
    <StoresModal v-if="modal" :store-names="storeNames" @close="closeModal" />
  </div>
</template>

<script>
import cloneDeep from 'lodash.clonedeep'
import { calcUnit, calcUnits } from '../../../modules/colorTip'
import CompaniesAnalysisSurveysTableData from './TableData'
import StoresCount from '../StoresCount'
import StoresModal from '../StoresModal'
import { KkIcon } from '../../../components/base'

export default {
  name: 'CompaniesAnalysisSurveysTable',
  components: {
    StoresCount,
    StoresModal,
    CompaniesAnalysisSurveysTableData,
    KkIcon
  },
  props: {
    loading: {
      type: Boolean,
      required: true,
    },
    crossSearchFields: {
      type: Array,
      required: true,
    },
    staticHeads: {
      type: Array,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    searchItems: {
      type: Array,
      required: false,
    },
    showReviewLink: {
      type: Boolean,
      default: false,
    },
    surveyId: {
      type: Number,
      default: 0,
    },
    startDate: {
      type: String,
      required: true,
    },
    endDate: {
      type: String,
      required: true,
    }
  },
  emits: ['change-display-graphs', 'change-individual-analysis-field'],
  data() {
    return {
      sortKey: '',
      sortOrder: '',
      modal: false,
      storeNames: null,
      firstSearchFieldWidth: '0px',
      resizeObserver: null,
      scrollIntervalId: null,
      controller: {
        width: '100%',
        top: '0px',
        targetWidth: '0px',
        prev: false,
        next: false,
        scrollAmount: '0',
      },
    }
  },
  computed: {
    /**
     * @return { MultiUnitCalculatedObj }
     */
    panelColorClassCondition() {
      const npsList = this.items.map(v => {
        return v.questions.find(question => question.type === 'nps')
      })
      const scores = npsList.map(nps => nps.value.score)
      const max = Math.max(...scores)
      const min = Math.min(...scores)
      const unit = calcUnit(max, min)
      const units = calcUnits({
        max,
        min,
        unit
      })
      return {
        max,
        min,
        units
      }
    },
    headItem() {
      if (this.items.length === 0) return undefined

      // TODO: チェックボックスにidを渡すようにする
      // チェックボックスのv-modelにオブジェクトを渡していて、参照が変わるとチェックが外れるのであえてdeep copyしない
      const item = this.items[0]
      item.questions = item.questions.map(question => {
        if (question.type === 'nps') return question

        // thの選択肢と対応する並び順で表示されるようにする
        const options = this.questionsExceptNps.find(head => head.id === question.id).options
        question.options = options.map(option => {
          return question.options.find(questionOption => questionOption.id === option.id)
        })
        return question
      })
      return item
    },
    /**
     * @return {Question[]}
     */
    staticHeadsExceptAnsweredCount() {
      return this.staticHeads.slice(1)
    },
    questionsExceptNps() {
      return this.staticHeadsExceptAnsweredCount.filter(v => v.type !== 'nps')
    },
    /**
     * @return { { id: number, name: string, headId: string, key: number }[] }
     */
    options() {
      return this.questionsExceptNps
        .map(head => {
          return head.options.map((option,key) => {
            option.headId = head.name + head.id
            option.key = key
            return option
          })
        })
        .flat()
    },
    answeredCount() {
      return this.staticHeads[0]
    },
    itemList() {
      let items = this.items.slice(1)
      items = items.map(item => {
        item.questions = item.questions.map(question => {
          let answerItems = []
          if (question.type === 'nps') {
            answerItems.push({
              key: 'question_' + question.id + 'nps',
              number: question.value.score,
              colorTipCondition: this.panelColorClassCondition
            })
          } else {
            // thの選択肢と対応する並び順で表示されるようにする
            const options = this.questionsExceptNps.find(head => head.id === question.id).options
            answerItems = options.map(option => {
              return {
                key: 'question_' + question.id +'_option_' + option.id,
                number: question.options.find(questionOption => questionOption.id === option.id).value,
              }
            })
          }
          question.answerItems = answerItems
          return question
        })
        return item
      })
      return items
    },
    sortedItemList() {
      let items = cloneDeep(this.itemList)

      switch (this.sortOrder) {
      case 'asc':
        items = items.sort((a, b) => {
          if (a[this.sortKey] < b[this.sortKey]) return -1
          if (a[this.sortKey] > b[this.sortKey]) return 1
          return 0
        })
        break
      case 'desc':
        items = items.sort((a, b) => {
          if (a[this.sortKey] > b[this.sortKey]) return -1
          if (a[this.sortKey] < b[this.sortKey]) return 1
          return 0
        })
        break
      default: // 最初は分析軸の名前でソート
        if (!items.length) break

        // クロス分析の場合、セカンダリーからソートさせるためソート順の配列を用意して外側から順にソート
        var sortIndexOrderList = items[0].fields.map((_, i) => i).reverse()
        sortIndexOrderList.forEach((i) => {
          items = items.sort((a, b) => {
            // 未設定(id=-1)は一番下になるよう判定
            if (Number(a.fields[i].id) === -1) return 1
            if (Number(b.fields[i].id) === -1) return -1

            if (a.fields[i].name < b.fields[i].name) return -1
            if (a.fields[i].name > b.fields[i].name) return 1
            return 0
          })
        })
      }

      return items
    },
    displayCheckbox() {
      return !!this.searchItems
    },
    innerSearchItems: {
      get() {
        return this.searchItems
      },
      set(val) {
        this.$emit('change-display-graphs', val)
      }
    },
    isStoreAnalysisAxis() {
      return this.crossSearchFields && this.crossSearchFields.filter((field) => field.id === 'store').length !== 0
    },
    companyUuid() {
      return this.$route.params.company_uuid
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
    this.setFirstSearchFieldWidth()
    this.tableObserver()
  },
  updated() {
    this.setFirstSearchFieldWidth()
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
    this.resizeObserver.disconnect()
    clearInterval(this.scrollIntervalId)
  },
  methods: {
    scrollPrevStart() {
      const $container = this.$refs.container
      this.scrollIntervalId = setInterval(() => {
        if ($container.scrollLeft === 0) {
          this.controller.prev = false
          clearInterval(this.scrollIntervalId)
        } else {
          $container.scrollLeft = $container.scrollLeft - 10
          this.controller.next = true
        }
      }, 10)
    },
    scrollNextStart() {
      const $container = this.$refs.container
      this.scrollIntervalId = setInterval(() => {
        if ($container.scrollLeft >= this.controller.scrollAmount) {
          this.controller.next = false
          clearInterval(this.scrollIntervalId)
        } else {
          $container.scrollLeft = $container.scrollLeft + 10
          this.controller.prev = true
        }
      }, 10)
    },
    scrollEnd() {
      clearInterval(this.scrollIntervalId)
    },
    tableObserver() {
      const $container = this.$refs.container
      const $table = this.$refs.table
      const $thead = this.$refs.thead
      const $tbody = this.$refs.tbody
      if (this.resizeObserver) return
      this.resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          if (entry.target.nodeName === 'TABLE') {
            if (!$tbody.firstElementChild) return
            this.controller.top = `${ $thead.offsetHeight + $tbody.firstElementChild.offsetHeight}px`
          }
          const $tr = $tbody.firstElementChild
          const $td = $tr.children

          // コントローラー幅の計算
          const controllerWidth = Array.from($td).reduce((prev, current) => {
            return current.classList.contains('fixed') ? prev - current.offsetWidth : prev
          }, $container.offsetWidth)
          this.controller.width = `${ controllerWidth }px`

          // スクロール可能か
          if ($container.clientWidth < $table.clientWidth) this.controller.next = true

          // スクロール可能な量の計算
          const fixedWidth = Array.from($td).reduce((prev, current) => {
            return current.classList.contains('fixed') ? prev + current.offsetWidth : prev
          }, 0)
          this.controller.scrollAmount = $table.clientWidth - fixedWidth - controllerWidth
        }
      })
      this.resizeObserver.observe($table)
      this.resizeObserver.observe($tbody)
      this.resizeObserver.observe($container)
    },
    /**
     * @param { Question }head
     * @return {number}
     */
    calcColspan(head) {
      if (head.type === 'nps') return 1
      return head.options.length
    },
    /**
     * @param { Question }head
     * @return {number}
     */
    calcRowspan(head) {
      return head.type === 'nps' ? 2 : 1
    },
    changeIndividualAnalysisField(field) {
      this.$emit('change-individual-analysis-field', field)
    },
    clearSelection() {
      this.$emit('change-display-graphs', [this.items[0]])
    },
    /**
     * @param { Question } head
     * @return {string}
     */
    tableHeadText(head) {
      if (head.type === 'nps') return `(${this.$t('models.survey.evaluationType.nps')})`
      const optionType = this.$t('components.CompaniesAnalysisSurveysTable.' + head.type)
      return `(${this.$t('components.CompaniesAnalysisSurveysTable.options', { count: head.options.length })} - ${optionType})`
    },
    sortTables(sortKey) {
      if (this.sortKey === sortKey) { // key が一致する場合は2回目押下時
        this.sortOrder = this.sortOrder === 'desc' ? 'asc' : 'desc'
      } else { // それ以外の場合は key 1回目押下時
        this.sortKey = sortKey
        this.sortOrder = 'desc'
      }
    },
    orderCss(key) {
      if (!this.sortOrder) return '' // 初期値の場合は CSS なし
      if (this.sortKey === key) { // 一致する key の場合に CSS がつく
        return this.sortOrder === 'asc' ? 'is-asc' : 'is-desc'
      } else { // それ以外の key の場合は CSS なし
        return ''
      }
    },
    openModal(storeNames) {
      this.storeNames = storeNames
      this.modal = true
    },
    closeModal() {
      this.storeNames = null
      this.modal = false
    },
    handleResize() {
      this.setFirstSearchFieldWidth()
    },
    setFirstSearchFieldWidth() {
      const searchFields = this.$refs.searchFields
      if(!searchFields) return
      if(this.firstSearchFieldWidth === window.getComputedStyle(searchFields[0]).width) return
      this.firstSearchFieldWidth = window.getComputedStyle(searchFields[0]).width
    },
  }
}
</script>

<style lang="scss" scoped>
.clearSelection {
  margin-left: 8px;
  button {
    font-size: 12px;
    padding: 16px;
    font-weight: normal;
  }
}

.analysis-table {
  position: relative;
  table {
    position: relative;
    z-index: 0;
  }
  thead {
    position: sticky;
    top: 0;
    z-index: 2;
  }
  &-container {
    overflow-x: auto;
    overflow-y: auto;
    max-height: 84vh;
  }
  &-controller {
    position: absolute;
    right: 0;
    z-index: 3;
    &-prev,
    &-next {
      position: absolute;
      top: 0;
      opacity: .3;
    }
    &-prev {
      left: 0;
      transform: translate(-50%, -50%);
      width: 40px;
    }
    &-next {
      right: 0;
      transform: translate(50%, -50%);
      width: 40px;
    }
    @media screen and (max-width: 768px) {
      display: none;
    }
  }
}

.head-item {
  .u-taR {
    background-color: #F8F9FA;
    font-size: 18px;
    font-weight: bold;
  }
}

tbody .table-head {
  background-color: #F8F9FA;
  border-right: 1px solid #E4E7EB;
}

.fixed {
  position: sticky;
  left: 0;
  z-index: 1;
  @media screen and (max-width: 768px) {
    position: initial;
  }
}

.index-number {
  font-weight: bold;
  margin: 0 8px;
}

.score-text {
  width: 38px;
}
.questionNameTh {
  border-left: 2px solid #fff;
  border-bottom: 1px solid #fff;
}
.optionTh {
  border-left: 1px solid #fff;
}
.optionTdFirst {
  border-left: 2px solid #E4E7EB;
}
.optionThFirst {
  border-left: 2px solid #fff;
}
.answeredCountTh {
  border-left: 2px solid #fff;
}
.survey-link {
  width: 24px;
  margin-left: auto;
  padding-left: 8px;
}
.table-container {
  display: flex;
  align-items: center;
}
</style>
